<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, onMounted, reactive, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useMachine } from "@xstate/vue"
import { createPdlSearchMachine } from "./machine/pdl-search.machine"
import { coreServiceRegistry } from "@core/services/register"
import type { OwnerOutput } from "@core/composables"
import {
    doesOwnerEmailMatchConsentForShare,
    pdlEnedisValidator,
    pdlValidator,
} from "@core/helpers"
import type { Address } from "@types"
import type { BaseSummaryLine } from "@core/components/base/summary/BaseSummary.vue"

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    /** Adresse du bâtiment pour la recherche PDL */
    address?: Address | null
    /** Titulaire (particulier ou entreprise) pour l’API recherche Enedis */
    owner?: OwnerOutput
    /** Contexte invitation (parcours titulaire externe) : repassé dans l'emit exist pour le routage machine */
    invitation?: boolean
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<Props>(), {
    address: undefined,
    owner: undefined,
    invitation: undefined,
})

/** Payload unique de l’emit exist : indique si le compteur existe et garde les mêmes infos (pdl, consentementUser, invitation) */
export type PdlFormSearchExistPayload = {
    /** true si le PDL existe en base avec consentement valide, false sinon */
    exists: boolean
    pdl: string
    invitation?: boolean
    /** Présent quand exists === true : true si l’utilisateur a accès (consentement) */
    consentementUser?: boolean
    /** Présent quand exists === true et consentementUser : id de la source existante (pour SourceAlreadyExist) */
    _id_source?: string
    /**
     * Présent quand exists === true : true si l’email titulaire saisi = signataire/titulaire du consentement (demande SHARE au lieu de SIGN).
     */
    sameTitulaireEmail?: boolean
}

const emit = defineEmits<{
    back: []
    exist: [payload: PdlFormSearchExistPayload]
}>()

// ================================
// SERVICES & MACHINE
// ================================

const injectedServices = inject<{
    bati?: typeof coreServiceRegistry.bati
    enedis?: typeof coreServiceRegistry.enedis
}>("services")
const batiService = injectedServices?.bati ?? coreServiceRegistry.bati
const enedisService = injectedServices?.enedis ?? coreServiceRegistry.enedis

const machine = createPdlSearchMachine({
    address: props.address,
    owner: props.owner,
    batiService,
    enedisService,
})
const { snapshot, send } = useMachine(machine)

const isStorybook = import.meta.env.STORYBOOK === "true"
const machineStateForStorybook = isStorybook
    ? reactive<{ value: unknown; context: unknown }>({
          value: undefined,
          context: undefined,
      })
    : undefined

if (isStorybook && machineStateForStorybook) {
    watch(
        snapshot,
        (s) => {
            machineStateForStorybook.value = s.value
            machineStateForStorybook.context = s.context
        },
        { immediate: true }
    )
}

defineExpose(
    isStorybook && machineStateForStorybook
        ? { machineState: machineStateForStorybook }
        : {}
)

// ================================
// DERIVED STATE
// ================================

const stateName = computed(() => {
    const v = snapshot.value.value
    return typeof v === "string" ? v : ""
})

const isSearching = computed(
    () => stateName.value === "geocoding" || stateName.value === "searching"
)
const isVerifying = computed(() => stateName.value === "verifying")
const searchDoneWithResult = computed(
    () => stateName.value === "searchDoneWithResult"
)
const searchDoneWithoutResult = computed(
    () => stateName.value === "searchDoneWithoutResult"
)
const searchDone = computed(
    () => searchDoneWithResult.value || searchDoneWithoutResult.value
)
const isError = computed(() => stateName.value === "error")

const context = computed(() => snapshot.value.context)
const pdlId = computed({
    get: () => context.value.pdlId ?? "",
    set: (value: string) => send({ type: "INPUT_PDL", value }),
})

/** PDL saisi : espaces ignorés (aligné machine / API) */
function normalizePdlSearchInput(raw: string): string {
    return raw.trim().replace(/\s+/g, "")
}

const normalizedPdlId = computed(() => normalizePdlSearchInput(pdlId.value))

/** idsource PDL : exactement 14 chiffres (PRM) */
const isPdlIdValid = computed(() => pdlValidator(normalizedPdlId.value))

/**
 * Avertissement Enedis : 14 chiffres valides mais préfixe PRM hors plages habituelles.
 */
const showPdlNotEnedisHint = computed(
    () => isPdlIdValid.value && !pdlEnedisValidator(normalizedPdlId.value)
)

/** Saisie non vide mais format PDL invalide */
const showPdlFormatInvalidHint = computed(
    () => pdlId.value.trim() !== "" && !isPdlIdValid.value
)

// ================================
// SUMMARY LINES (BaseSummary)
// ================================

function getSummaryLines(): BaseSummaryLine[] {
    const ctx = context.value
    const lines: BaseSummaryLine[] = []
    if (ctx.owner) {
        const name =
            ctx.owner.company != null
                ? ctx.owner.company.name
                : [ctx.owner.user?.firstname, ctx.owner.user?.lastname]
                      .filter(Boolean)
                      .join(" ")
        lines.push({ label: "Titulaire", value: name || "—" })
        const siret = ctx.owner.company?.idcompany?.trim()
        if (siret) {
            lines.push({ label: "SIRET", value: siret })
        }
    }
    if (ctx.address) {
        const addr = ctx.address
        const formatted = [addr.street, addr.zipcode, addr.city]
            .filter(Boolean)
            .join(", ")
        lines.push({ label: "Adresse", value: formatted || "—" })
    }
    if (isSearching.value) {
        lines.push({ label: "PDL", value: "Recherche en cours…" })
    } else if (ctx.searchResult != null && ctx.pdlId) {
        lines.push({ label: "PDL", value: ctx.pdlId })
        if (ctx.contractName) {
            lines.push({ label: "Nom du contrat", value: ctx.contractName })
        }
    } else if (searchDone.value && ctx.pdlId) {
        lines.push({ label: "PDL", value: ctx.pdlId })
    }
    return lines
}

const summaryLines = computed(() => getSummaryLines())

// ================================
// EMIT ON DONE
// ================================

watch(
    [stateName, () => context.value.verifyResult],
    ([name, verifyResult]) => {
        if (name !== "done" || verifyResult === undefined) return
        const pdl = normalizePdlSearchInput(context.value.pdlId ?? "")
        if (verifyResult === false) {
            emit("exist", { exists: false, pdl, invitation: props.invitation })
            return
        }
        const sourceId =
            typeof verifyResult === "object" && verifyResult?.source?._id
                ? verifyResult.source._id
                : undefined
        const consentementUser = verifyResult.hasAccess ?? false
        emit("exist", {
            exists: true,
            pdl,
            consentementUser,
            invitation: props.invitation,
            _id_source: sourceId,
            sameTitulaireEmail:
                !consentementUser &&
                doesOwnerEmailMatchConsentForShare(
                    props.owner,
                    verifyResult.consentement
                ),
        })
    },
    { flush: "sync" }
)

// ================================
// LIFECYCLE & ACTIONS
// ================================

onMounted(() => {
    send({ type: "START" })
})

function onBack(): void {
    emit("back")
}

function onVerify(): void {
    send({ type: "VERIFY" })
}

function onRetry(): void {
    send({ type: "RETRY" })
}

// ================================
// I18N
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Recherche du point de livraison (PDL)",
            placeholder: "Identifiant PDL",
            verify: "Vérifier",
            back: "Retour",
            retry: "Réessayer",
            search_loading: "Recherche en cours…",
            message_pdl_found:
                "Le numéro de PDL a été trouvé pour cette adresse.",
            message_no_pdl:
                "Aucun numéro de PDL trouvé pour cette adresse et ce titulaire. Vérifiez les informations du titulaire ou saisissez le numéro manuellement.",
            hint_pdl_format_guide:
                "Saisissez le numéro PDL (PRM) : 14 chiffres. Les espaces sont ignorés.",
            hint_pdl_format_invalid:
                "Le numéro doit comporter exactement 14 chiffres.",
            hint_pdl_not_enedis:
                "Ce numéro ne correspond pas au format habituel d’un PDL Enedis (PRM). Vérifiez votre saisie.",
        },
    },
})

/** Messages d’erreur sous le hint (BaseInputWrapper, HTML avec sauts de ligne) */
const getPdlIdsourceWrapperError = computed(() => {
    const parts: string[] = []
    if (showPdlFormatInvalidHint.value) {
        parts.push(t("hint_pdl_format_invalid"))
    }
    if (showPdlNotEnedisHint.value) {
        parts.push(t("hint_pdl_not_enedis"))
    }
    return parts.length > 0 ? parts.join("<br>") : ""
})
</script>

<template>
    <div class="pdl-form-search flex flex-col gap-4">
        <header class="w-full text-center">
            <h2 class="text-lg font-semibold text-gray-700 text-center">
                {{ t("title") }}
            </h2>
        </header>

        <!-- BaseSummary toujours affiché dès qu’il y a des lignes -->
        <BaseSummary
            v-if="summaryLines.length > 0"
            :lines="summaryLines"
            variant="grey"
        />

        <!-- Pendant la recherche -->
        <div
            v-if="isSearching"
            class="flex items-center gap-2 text-sm text-gray-600 mx-auto"
        >
            <BaseProgressSpinner
                style="width: 2rem; height: 2rem"
                stroke-width="8"
                fill="transparent"
                animation-duration=".5s"
                class="w-fit mx-0!"
            />
            <span>{{ t("search_loading") }}</span>
        </div>

        <!-- Après recherche ou pendant vérification : message + input + bouton Vérifier -->
        <template v-if="searchDone || isVerifying">
            <p v-if="searchDoneWithResult" class="text-sm text-green-700">
                {{ t("message_pdl_found") }}
            </p>
            <p
                v-else-if="searchDoneWithoutResult"
                class="text-sm text-amber-700"
            >
                {{ t("message_no_pdl") }}
            </p>

            <BaseInputWrapper
                input-id="pdl-form-search-idsource"
                :label="t('placeholder')"
                :hint="t('hint_pdl_format_guide')"
                :error="getPdlIdsourceWrapperError"
                class="w-full"
            >
                <BaseInputText
                    v-model="pdlId"
                    input-id="pdl-form-search-idsource"
                    fluid
                    :placeholder="t('placeholder')"
                    :disabled="isVerifying"
                    :invalid="!!getPdlIdsourceWrapperError"
                    :class="{ 'p-invalid': !!getPdlIdsourceWrapperError }"
                    autocomplete="off"
                />
            </BaseInputWrapper>

            <div
                class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
            >
                <BaseButton
                    :label="t('back')"
                    severity="secondary"
                    @click="onBack"
                    :disabled="isVerifying"
                    class="min-w-[10rem]"
                />
                <BaseButton
                    :label="isError ? t('retry') : t('verify')"
                    :loading="isVerifying"
                    :disabled="!isPdlIdValid || isVerifying"
                    @click="isError ? onRetry() : onVerify()"
                    class="min-w-[10rem]"
                />
            </div>
        </template>

        <!-- État erreur après VERIFY (RETRY) -->
        <template v-else-if="isError">
            <p class="text-sm text-red-600">
                Une erreur est survenue lors de la vérification.
            </p>
            <BaseInputWrapper
                input-id="pdl-form-search-idsource"
                :label="t('placeholder')"
                :hint="t('hint_pdl_format_guide')"
                :error="getPdlIdsourceWrapperError"
                class="w-full"
            >
                <BaseInputText
                    v-model="pdlId"
                    input-id="pdl-form-search-idsource"
                    fluid
                    :placeholder="t('placeholder')"
                    :invalid="!!getPdlIdsourceWrapperError"
                    :class="{ 'p-invalid': !!getPdlIdsourceWrapperError }"
                    autocomplete="off"
                />
            </BaseInputWrapper>
            <div class="flex gap-3">
                <BaseButton
                    :label="t('back')"
                    severity="secondary"
                    @click="onBack"
                />
                <BaseButton
                    :label="t('retry')"
                    :disabled="!isPdlIdValid"
                    @click="onRetry"
                />
            </div>
        </template>

        <!-- idle (avant que START n’ait été traité) : afficher loader ou rien -->
        <div
            v-else-if="stateName === 'idle'"
            class="flex items-center gap-2 text-sm text-gray-500"
        >
            <BaseProgressSpinner class="h-5 w-5" />
            <span>{{ t("search_loading") }}</span>
        </div>
    </div>
</template>
